import { initializeApp, FirebaseApp, getApps } from 'firebase/app';
import { getAuth, Auth } from 'firebase/auth';
import { getFirestore, Firestore } from 'firebase/firestore';
import { EnvironmentConfig } from './environments';

// Store multiple Firebase app instances
const firebaseApps: Map<string, FirebaseApp> = new Map();

// Store auth and firestore instances per environment
let currentApp: FirebaseApp | null = null;
let currentAuth: Auth | null = null;
let currentDb: Firestore | null = null;

// Track current environment name
let currentEnvironmentName: string | null = null;

/**
 * Initialize Firebase for a specific environment
 */
export const initializeFirebase = (env: EnvironmentConfig): void => {
  // If switching to a different environment, clear the current app
  if (currentEnvironmentName && currentEnvironmentName !== env.name) {
    console.log(`Switching from ${currentEnvironmentName} to ${env.name}`);
    // Clear existing app references
    currentApp = null;
    currentAuth = null;
    currentDb = null;
  }

  // Check if this environment's app already exists
  if (firebaseApps.has(env.name)) {
    currentApp = firebaseApps.get(env.name)!;
    console.log(`Using existing Firebase app for ${env.displayName}`);
  } else {
    // Initialize the default app with the environment's config
    const apps = getApps();
    if (apps.length === 0 || currentEnvironmentName !== env.name) {
      // Clear any existing default app to force re-initialization
      if (apps.length > 0) {
        // Firebase doesn't allow deleting the default app, so we need to reload the page
        // This is handled in EnvironmentContext
        console.log(`Need to reload to switch from ${apps[0].name} to ${env.name}`);
      }
      
      currentApp = initializeApp(env.firebase);
      console.log(`Initialized DEFAULT Firebase app for ${env.displayName} (${env.firebase.projectId})`);
    } else {
      currentApp = apps[0];
      console.log(`Reusing existing DEFAULT Firebase app for ${env.displayName}`);
    }
    
    // Store the app instance
    firebaseApps.set(env.name, currentApp);
  }

  // Update current environment name
  currentEnvironmentName = env.name;

  // Get auth and firestore for current app
  currentAuth = getAuth(currentApp);
  currentDb = getFirestore(currentApp);
};

/**
 * Get current Firebase Auth instance
 */
export const getCurrentAuth = (): Auth => {
  if (!currentAuth) {
    throw new Error('Firebase Auth not initialized. Call initializeFirebase first.');
  }
  return currentAuth;
};

/**
 * Get current Firestore instance
 */
export const getCurrentDb = (): Firestore => {
  if (!currentDb) {
    throw new Error('Firestore not initialized. Call initializeFirebase first.');
  }
  return currentDb;
};

/**
 * Get current Firebase App instance
 */
export const getCurrentApp = (): FirebaseApp => {
  if (!currentApp) {
    throw new Error('Firebase App not initialized. Call initializeFirebase first.');
  }
  return currentApp;
};

/**
 * Check if a specific environment's Firebase is initialized
 */
export const isEnvironmentInitialized = (envName: string): boolean => {
  return firebaseApps.has(envName);
};

/**
 * Clear all Firebase instances (useful for cleanup)
 */
export const clearAllFirebaseInstances = (): void => {
  firebaseApps.clear();
  currentApp = null;
  currentAuth = null;
  currentDb = null;
};

